// src/ShopifyOnboardingPage.js
import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './ShopifyOnboardingPage.css';
import { useI18n } from './i18n';

const ShopifyOnboardingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const shop = searchParams.get('shop');
  const { t, lang, toggleLang } = useI18n();
  
  const [formData, setFormData] = useState({
    brandName: '',
    email: '',
    password: '',
    telegramId: '',
    countryCode: '20',
    facebookUrl: '',
    instagramUrl: '',
    messageLanguage: 'ar'
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorKey, setErrorKey] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'telegramId' ? value.replace(/\D/g, '') : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorKey('');
    setLoading(true);

    try {
      // Validation
      if (!formData.brandName || !formData.email || !formData.password || !formData.telegramId) {
        setErrorKey('onboarding.error.missingFields');
        setLoading(false);
        return;
      }

      if (formData.password.length < 6) {
        setErrorKey('onboarding.error.passwordShort');
        setLoading(false);
        return;
      }

      console.log('🔄 Completing Shopify onboarding for:', shop);

      // استدعاء Cloud Function لإكمال الـ onboarding
      const functions = getFunctions(undefined, 'us-central1');
      const completeOnboarding = httpsCallable(functions, 'completeShopifyOnboarding');
      
      const result = await completeOnboarding({
        shop: shop,
        brandName: formData.brandName,
        email: formData.email,
        password: formData.password,
        telegramId: formData.telegramId,
        countryCode: formData.countryCode,
        facebookUrl: formData.facebookUrl || '',
        instagramUrl: formData.instagramUrl || '',
        messageLanguage: formData.messageLanguage || 'ar'
      });

      if (!result.data.success) {
        setError(result.data.message || '');
        setErrorKey(result.data.message ? '' : 'onboarding.error.generic');
        throw new Error(result.data.message || 'onboarding.error.generic');
      }

      console.log('✅ Onboarding completed successfully:', result.data);

      // 🔐 حفظ الإيميل والباسورد في localStorage للـ auto-login
      if (result.data.email && result.data.password) {
        console.log('💾 Saving credentials to localStorage for auto-login');
        localStorage.setItem('shopify_auto_email', result.data.email);
        localStorage.setItem('shopify_auto_password', result.data.password);
        localStorage.setItem('shopify_auth_uid', result.data.authUid);
      }

      // �🔄 Redirect to dashboard with auth_uid
      const dashboardUrl = `https://dashboard-daf-api.dafaiagency.shop/dashboard?uid=${result.data.authUid}`;
      console.log('🔗 Redirecting to:', dashboardUrl);
      
      // Direct redirect to dashboard
      window.location.href = dashboardUrl;
      
    } catch (err) {
      console.error('❌ Onboarding error:', err);
      const message = err?.message;
      if (message && message.startsWith('onboarding.')) {
        setErrorKey(message);
        setError('');
      } else {
        setError(message || '');
        setErrorKey(message ? '' : 'onboarding.error.generic');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!shop) {
    return (
      <div className="onboarding-container">
        <div className="onboarding-card">
          <h1 style={{ color: '#ff5555' }}>❌</h1>
          <p>{t('onboarding.error.missingShop')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
          <button
            type="button"
            onClick={toggleLang}
            style={{
              background: '#2d2f3a',
              color: '#f8f8f2',
              border: '1px solid #444',
              borderRadius: '8px',
              padding: '8px 12px',
              cursor: 'pointer',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            aria-label={t('lang.toggle')}
          >
            <span aria-hidden="true">🌐</span>
            <span>{lang === 'en' ? 'EN' : 'AR'}</span>
          </button>
        </div>
        <div className="onboarding-header">
          <h1>🎉 {t('onboarding.title')}</h1>
          <p className="shop-name">{shop}</p>
          <p className="subtitle">{t('onboarding.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="onboarding-form">
          {(error || errorKey) && (
            <div className="error-message">
              ⚠️ {errorKey ? t(errorKey) : error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="brandName">
              {t('onboarding.brandName')} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="brandName"
              name="brandName"
              value={formData.brandName}
              onChange={handleInputChange}
              placeholder={t('onboarding.brandName.placeholder')}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">
              {t('onboarding.email')} <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={t('onboarding.email.placeholder')}
              required
              disabled={loading}
            />
            <small className="field-hint">✅ {t('onboarding.email.hint')}</small>
          </div>

          <div className="form-group">
            <label htmlFor="password">
              {t('onboarding.password')} <span className="required">*</span>
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder={t('onboarding.password.placeholder')}
              required
              minLength="6"
              disabled={loading}
            />
            <small className="field-hint">{t('onboarding.password.hint')}</small>
          </div>

          <div className="form-group">
            <label htmlFor="telegramId">
              {t('onboarding.telegramId')} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="telegramId"
              name="telegramId"
              value={formData.telegramId}
              onChange={handleInputChange}
              placeholder={t('onboarding.telegramId.placeholder')}
              required
              disabled={loading}
            />
            <small className="field-hint">
              {t('onboarding.telegramId.hint').replace('@get_myidrobot', '')}
              <a href="https://t.me/get_myidrobot" target="_blank" rel="noopener noreferrer" className="telegram-link">@get_myidrobot</a>
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="facebookUrl">
              {t('onboarding.facebook')}
            </label>
            <input
              type="url"
              id="facebookUrl"
              name="facebookUrl"
              value={formData.facebookUrl}
              onChange={handleInputChange}
              placeholder={t('onboarding.facebook.placeholder')}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="instagramUrl">
              {t('onboarding.instagram')}
            </label>
            <input
              type="url"
              id="instagramUrl"
              name="instagramUrl"
              value={formData.instagramUrl}
              onChange={handleInputChange}
              placeholder={t('onboarding.instagram.placeholder')}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>{t('onboarding.messageLanguage')} <span className="required">*</span></label>
            <small className="field-hint" style={{ display: 'block', marginBottom: '10px' }}>
              {t('onboarding.messageLanguage.hint')}
            </small>
            <div style={{ display: 'flex', gap: '12px' }}>
              <label
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px',
                  borderRadius: '10px',
                  border: formData.messageLanguage === 'ar' ? '2px solid #6272a4' : '2px solid #44475a',
                  background: formData.messageLanguage === 'ar' ? 'rgba(98,114,164,0.2)' : 'transparent',
                  cursor: 'pointer',
                  fontWeight: 600,
                  color: '#f8f8f2',
                  transition: 'all 0.2s',
                }}
              >
                <input
                  type="radio"
                  name="messageLanguage"
                  value="ar"
                  checked={formData.messageLanguage === 'ar'}
                  onChange={handleInputChange}
                  disabled={loading}
                  style={{ display: 'none' }}
                />
                {t('onboarding.messageLanguage.ar')}
              </label>
              <label
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px',
                  borderRadius: '10px',
                  border: formData.messageLanguage === 'en' ? '2px solid #6272a4' : '2px solid #44475a',
                  background: formData.messageLanguage === 'en' ? 'rgba(98,114,164,0.2)' : 'transparent',
                  cursor: 'pointer',
                  fontWeight: 600,
                  color: '#f8f8f2',
                  transition: 'all 0.2s',
                }}
              >
                <input
                  type="radio"
                  name="messageLanguage"
                  value="en"
                  checked={formData.messageLanguage === 'en'}
                  onChange={handleInputChange}
                  disabled={loading}
                  style={{ display: 'none' }}
                />
                {t('onboarding.messageLanguage.en')}
              </label>
            </div>
          </div>

          <button 
            type="submit" 
            className="submit-button"
            disabled={loading}
          >
            {loading ? `⏳ ${t('onboarding.submitting')}` : `✅ ${t('onboarding.submit')}`}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ShopifyOnboardingPage;
